import { assertProjectWriteAccess, assertSkillWriteAccess, normalizeZeroDateArray, syncProjectFunctionSeriesDays, toZeroDate, toZeroDateArray, } from "../projects-helpers.js";
import { createDefaultFunctionGroupInputSchema, createProjectFunctionGroupInputSchema, defaultFunctionGroupIDInputSchema, deleteProjectFunctionGroupInputSchema, deleteProjectPersonTravelDayInputSchema, updateDefaultFunctionGroupInputSchema, updateProjectFunctionGroupInputSchema, upsertProjectPersonTravelDayInputSchema, } from "../projects-input-schemas.js";
function authorizedDefaultFunctionsQuery(ctx, projectZql) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.defaultFunctions.where("id", "__unauthorized__");
    }
    return projectZql.defaultFunctions;
}
function authorizedDefaultFunctionGroupsQuery(ctx, projectZql) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.defaultFunctionGroups.where("id", "__unauthorized__");
    }
    return projectZql.defaultFunctionGroups;
}
function authorizedProjectFunctionGroupsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.projectFunctionGroups;
    }
    return projectZql.projectFunctionGroups.where("id", "__unauthorized__");
}
function authorizedProjectPersonTravelDaysQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.projectPersonTravelDays;
    }
    return ctx.personID
        ? projectZql.projectPersonTravelDays.where("personID", ctx.personID)
        : projectZql.projectPersonTravelDays.where("id", "__unauthorized__");
}
function authorizedSkillRatingsQuery(ctx, projectZql) {
    if (ctx.role !== "admin" && ctx.role !== "employee") {
        return projectZql.personSkillRatings.where("id", "__unauthorized__");
    }
    return projectZql.personSkillRatings;
}
function authorizedProjectAvailabilityQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.projectAvailability;
    }
    return ctx.personID
        ? projectZql.projectAvailability.where("personID", ctx.personID)
        : projectZql.projectAvailability.where("id", "__unauthorized__");
}
function authorizedProjectFunctionSeriesQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.projectFunctionSeries;
    }
    return projectZql.projectFunctionSeries.where("id", "__unauthorized__");
}
function authorizedFunctionAssignmentsQuery(ctx, projectZql) {
    if (ctx.role === "admin" || ctx.role === "employee") {
        return projectZql.functionAssignments;
    }
    return ctx.personID
        ? projectZql.functionAssignments.where("personID", ctx.personID)
        : projectZql.functionAssignments.where("id", "__unauthorized__");
}
export function createPeoplePlanningQueries({ defineProjectQuery, projectZql, }) {
    return {
        defaultFunctions: {
            list: defineProjectQuery(({ ctx }) => authorizedDefaultFunctionsQuery(ctx, projectZql)
                .related("defaultFunctionGroup")
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc")),
        },
        defaultFunctionGroups: {
            list: defineProjectQuery(({ ctx }) => authorizedDefaultFunctionGroupsQuery(ctx, projectZql)
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc")),
        },
        personSkillRatings: {
            byPerson: defineProjectQuery(({ args, ctx }) => authorizedSkillRatingsQuery(ctx, projectZql)
                .where("personID", args.personID)
                .related("defaultFunction")),
            allRatedAtLeast: defineProjectQuery(({ args, ctx }) => authorizedSkillRatingsQuery(ctx, projectZql)
                .where("rating", ">=", args.minRating)
                .related("defaultFunction")),
        },
        projectAvailability: {
            byProject: defineProjectQuery(({ args, ctx }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .related("person", (person) => person.related("personAllergyTags", (assignment) => assignment.related("allergyTag")))
                .orderBy("createdAt", "asc")),
            bookedByPerson: defineProjectQuery(({ args, ctx }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("personID", args.personID)
                .where("status", "booked")
                .related("project", (project) => project.where("deletedAt", "IS", null))
                .orderBy("createdAt", "asc")),
            bookedByPeople: defineProjectQuery(({ args, ctx, }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("personID", "IN", args.personIDs)
                .where("status", "booked")
                .related("project")
                .orderBy("createdAt", "asc")),
            pendingPlanning: defineProjectQuery(({ ctx }) => authorizedProjectAvailabilityQuery(ctx, projectZql)
                .where("status", "IN", ["unknown", "requested"])
                .related("person")
                .related("project", (project) => project.where("deletedAt", "IS", null))
                .orderBy("createdAt", "asc")),
        },
        projectContacts: {
            byProject: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.projectContacts.where("id", "__unauthorized__");
                }
                return projectZql.projectContacts
                    .where("projectID", args.projectID)
                    .related("person")
                    .orderBy("createdAt", "asc");
            }),
        },
        crewEmails: {
            byProject: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.crewEmails.where("id", "__unauthorized__");
                }
                return projectZql.crewEmails
                    .where("projectID", args.projectID)
                    .related("recipients", (recipient) => recipient.related("person"))
                    .related("attachments")
                    .orderBy("createdAt", "desc");
            }),
        },
        projectMealPlans: {
            byProject: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.projectMealPlans.where("id", "__unauthorized__");
                }
                return projectZql.projectMealPlans
                    .where("projectID", args.projectID)
                    .orderBy("planDate", "asc");
            }),
        },
        projectHotelPlans: {
            byProject: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.projectHotelPlans.where("id", "__unauthorized__");
                }
                return projectZql.projectHotelPlans
                    .where("projectID", args.projectID)
                    .orderBy("planDate", "asc");
            }),
        },
        networkCoverageSessions: {
            byProject: defineProjectQuery(({ args, ctx, }) => {
                if (ctx.role !== "admin" && ctx.role !== "employee") {
                    return projectZql.networkCoverageSessions.where("id", "__unauthorized__");
                }
                return projectZql.networkCoverageSessions
                    .where("projectID", args.projectID)
                    .orderBy("startedAt", "desc");
            }),
        },
        projectFunctionSeries: {
            byProject: defineProjectQuery(({ args, ctx }) => authorizedProjectFunctionSeriesQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .related("projectFunctionGroup")
                .related("days", (day) => day
                .related("assignments", (assignment) => assignment.related("person"))
                .orderBy("functionDate", "asc"))
                .orderBy("name", "asc")),
            listProjectIDs: defineProjectQuery(({ ctx }) => authorizedProjectFunctionSeriesQuery(ctx, projectZql).orderBy("projectID", "asc")),
        },
        projectFunctionGroups: {
            byProject: defineProjectQuery(({ args, ctx }) => authorizedProjectFunctionGroupsQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .orderBy("displayOrder", "asc")
                .orderBy("name", "asc")),
        },
        projectPersonTravelDays: {
            byProject: defineProjectQuery(({ args, ctx }) => authorizedProjectPersonTravelDaysQuery(ctx, projectZql)
                .where("projectID", args.projectID)
                .related("person")
                .orderBy("travelDate", "asc")),
        },
        functionAssignments: {
            byPerson: defineProjectQuery(({ args, ctx }) => authorizedFunctionAssignmentsQuery(ctx, projectZql)
                .where("personID", args.personID)
                .related("projectFunction", (projectFunction) => projectFunction.related("project"))
                .orderBy("createdAt", "asc")),
            byPeople: defineProjectQuery(({ args, ctx, }) => authorizedFunctionAssignmentsQuery(ctx, projectZql)
                .where("personID", "IN", args.personIDs)
                .related("projectFunction", (projectFunction) => projectFunction.related("project"))
                .orderBy("createdAt", "asc")),
        },
    };
}
export function createPeoplePlanningMutators({ defineProjectMutator, projectZql, schemas, }) {
    const { createDefaultFunctionInputSchema, updateDefaultFunctionInputSchema, defaultFunctionIDInputSchema, upsertSkillRatingInputSchema, deleteSkillRatingInputSchema, upsertProjectAvailabilityInputSchema, batchUpsertProjectAvailabilityInputSchema, deleteProjectAvailabilityInputSchema, updateProjectAvailabilityRemarkInputSchema, updateProjectAvailabilityUnityUsernameInputSchema, createProjectContactInputSchema, updateProjectContactInputSchema, deleteProjectContactInputSchema, upsertProjectMealPlanInputSchema, batchUpsertProjectMealPlansInputSchema, upsertProjectHotelPlanInputSchema, batchUpsertProjectHotelPlansInputSchema, linkNetworkCoverageSessionInputSchema, unlinkNetworkCoverageSessionInputSchema, createProjectFunctionSeriesInputSchema, updateProjectFunctionSeriesInputSchema, deleteProjectFunctionSeriesInputSchema, updateProjectFunctionDayInputSchema, createFunctionAssignmentInputSchema, bulkAssignFunctionSeriesInputSchema, updateFunctionAssignmentInputSchema, deleteFunctionAssignmentInputSchema, initializeProjectFunctionsInputSchema, } = schemas;
    return {
        projectAvailability: {
            upsert: defineProjectMutator(upsertProjectAvailabilityInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const existing = await tx.run(projectZql.projectAvailability
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .one());
                const row = {
                    id: existing?.id ?? args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    status: args.status,
                    availableDays: toZeroDateArray(args.availableDays),
                    notes: args.notes ?? null,
                };
                if (existing) {
                    await tx.mutate.projectAvailability.update(row);
                    return;
                }
                await tx.mutate.projectAvailability.insert(row);
            }),
            batchUpsert: defineProjectMutator(batchUpsertProjectAvailabilityInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const personID of args.personIDs) {
                    const existing = await tx.run(projectZql.projectAvailability
                        .where("projectID", args.projectID)
                        .where("personID", personID)
                        .one());
                    const row = {
                        id: existing?.id ?? crypto.randomUUID(),
                        projectID: args.projectID,
                        personID,
                        status: args.status,
                        availableDays: toZeroDateArray(args.availableDays),
                    };
                    if (existing) {
                        await tx.mutate.projectAvailability.update(row);
                    }
                    else {
                        await tx.mutate.projectAvailability.insert(row);
                    }
                }
            }),
            delete: defineProjectMutator(deleteProjectAvailabilityInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectAvailability.delete({ id: args.id });
            }),
            updateRemark: defineProjectMutator(updateProjectAvailabilityRemarkInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectAvailability.update({
                    id: args.id,
                    crewListRemark: args.crewListRemark ?? null,
                });
            }),
            updateUnityUsername: defineProjectMutator(updateProjectAvailabilityUnityUsernameInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectAvailability.update({
                    id: args.id,
                    unityUsername: args.unityUsername ?? null,
                });
            }),
        },
        projectContacts: {
            create: defineProjectMutator(createProjectContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectContacts.insert({
                    id: args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    description: args.description,
                });
            }),
            update: defineProjectMutator(updateProjectContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectContacts.update({
                    id: args.id,
                    ...(args.personID !== undefined ? { personID: args.personID } : {}),
                    ...(args.description !== undefined
                        ? { description: args.description }
                        : {}),
                });
            }),
            delete: defineProjectMutator(deleteProjectContactInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectContacts.delete({ id: args.id });
            }),
        },
        projectMealPlans: {
            upsert: defineProjectMutator(upsertProjectMealPlanInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const planDate = toZeroDate(args.planDate) ?? 0;
                const existing = await tx.run(projectZql.projectMealPlans
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .where("planDate", planDate)
                    .one());
                const row = {
                    id: existing?.id ?? args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    planDate,
                    lunch: args.lunch,
                    dinner: args.dinner,
                };
                if (existing) {
                    await tx.mutate.projectMealPlans.update(row);
                }
                else {
                    await tx.mutate.projectMealPlans.insert(row);
                }
            }),
            batchUpsert: defineProjectMutator(batchUpsertProjectMealPlansInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const plan of args.plans) {
                    const planDate = toZeroDate(plan.planDate) ?? 0;
                    const existing = await tx.run(projectZql.projectMealPlans
                        .where("projectID", args.projectID)
                        .where("personID", plan.personID)
                        .where("planDate", planDate)
                        .one());
                    const row = {
                        id: existing?.id ?? plan.id,
                        projectID: args.projectID,
                        personID: plan.personID,
                        planDate,
                        lunch: plan.lunch,
                        dinner: plan.dinner,
                    };
                    if (existing) {
                        await tx.mutate.projectMealPlans.update(row);
                    }
                    else {
                        await tx.mutate.projectMealPlans.insert(row);
                    }
                }
            }),
        },
        projectHotelPlans: {
            upsert: defineProjectMutator(upsertProjectHotelPlanInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const planDate = toZeroDate(args.planDate) ?? 0;
                const existing = await tx.run(projectZql.projectHotelPlans
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .where("planDate", planDate)
                    .one());
                const row = {
                    id: existing?.id ?? args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    planDate,
                    needsHotel: args.needsHotel,
                    roomType: args.needsHotel ? (args.roomType ?? "single") : "single",
                    roommatePersonID: args.needsHotel && args.roomType === "double"
                        ? (args.roommatePersonID ?? null)
                        : null,
                };
                if (existing) {
                    await tx.mutate.projectHotelPlans.update(row);
                }
                else {
                    await tx.mutate.projectHotelPlans.insert(row);
                }
            }),
            batchUpsert: defineProjectMutator(batchUpsertProjectHotelPlansInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const plan of args.plans) {
                    const planDate = toZeroDate(plan.planDate) ?? 0;
                    const existing = await tx.run(projectZql.projectHotelPlans
                        .where("projectID", args.projectID)
                        .where("personID", plan.personID)
                        .where("planDate", planDate)
                        .one());
                    const row = {
                        id: existing?.id ?? plan.id,
                        projectID: args.projectID,
                        personID: plan.personID,
                        planDate,
                        needsHotel: plan.needsHotel,
                        roomType: plan.needsHotel
                            ? (plan.roomType ?? "single")
                            : "single",
                        roommatePersonID: plan.needsHotel && plan.roomType === "double"
                            ? (plan.roommatePersonID ?? null)
                            : null,
                    };
                    if (existing) {
                        await tx.mutate.projectHotelPlans.update(row);
                    }
                    else {
                        await tx.mutate.projectHotelPlans.insert(row);
                    }
                }
            }),
        },
        networkCoverageSessions: {
            link: defineProjectMutator(linkNetworkCoverageSessionInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.networkCoverageSessions.insert({
                    id: args.id,
                    projectID: args.projectID,
                    externalSessionID: args.externalSessionID,
                    deviceName: args.deviceName ?? null,
                    deviceUID: args.deviceUID ?? null,
                    startedAt: args.startedAt,
                    endedAt: args.endedAt ?? null,
                    hasGpx: args.hasGpx ?? false,
                    summaryStats: args.summaryStats ?? null,
                });
            }),
            unlink: defineProjectMutator(unlinkNetworkCoverageSessionInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.networkCoverageSessions.delete({ id: args.id });
            }),
        },
        defaultFunctionGroups: {
            create: defineProjectMutator(createDefaultFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctionGroups.insert({
                    ...args,
                    locationType: args.locationType ?? null,
                    displayOrder: args.displayOrder ?? 0,
                });
            }),
            update: defineProjectMutator(updateDefaultFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctionGroups.update(args);
            }),
            delete: defineProjectMutator(defaultFunctionGroupIDInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctionGroups.delete({ id: args.id });
            }),
        },
        defaultFunctions: {
            create: defineProjectMutator(createDefaultFunctionInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctions.insert({
                    ...args,
                    defaultFunctionGroupID: args.defaultFunctionGroupID ?? null,
                    displayOrder: args.displayOrder ?? 0,
                    systemTag: args.systemTag ?? null,
                    locationType: args.locationType ?? null,
                    showOnInitialize: args.showOnInitialize ?? true,
                });
            }),
            update: defineProjectMutator(updateDefaultFunctionInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctions.update(args);
            }),
            delete: defineProjectMutator(defaultFunctionIDInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.defaultFunctions.delete({ id: args.id });
            }),
        },
        personSkillRatings: {
            upsert: defineProjectMutator(upsertSkillRatingInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                const existing = await tx.run(projectZql.personSkillRatings
                    .where("personID", args.personID)
                    .where("defaultFunctionID", args.defaultFunctionID)
                    .one());
                if (existing) {
                    await tx.mutate.personSkillRatings.update({
                        id: existing.id,
                        rating: args.rating,
                        notes: args.notes ?? null,
                    });
                    return;
                }
                await tx.mutate.personSkillRatings.insert({
                    ...args,
                    notes: args.notes ?? null,
                });
            }),
            delete: defineProjectMutator(deleteSkillRatingInputSchema, async ({ tx, ctx, args, }) => {
                assertSkillWriteAccess(ctx);
                await tx.mutate.personSkillRatings.delete({ id: args.id });
            }),
        },
        projectFunctionGroups: {
            create: defineProjectMutator(createProjectFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectFunctionGroups.insert({
                    ...args,
                    locationType: args.locationType ?? null,
                    displayOrder: args.displayOrder ?? 0,
                });
            }),
            update: defineProjectMutator(updateProjectFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectFunctionGroups.update(args);
            }),
            delete: defineProjectMutator(deleteProjectFunctionGroupInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectFunctionGroups.delete({ id: args.id });
            }),
        },
        projectFunctionSeries: {
            create: defineProjectMutator(createProjectFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const selectedDays = normalizeZeroDateArray(args.selectedDays);
                const requiredCount = args.requiredCount ?? 1;
                await tx.mutate.projectFunctionSeries.insert({
                    id: args.id,
                    projectID: args.projectID,
                    projectFunctionGroupID: args.projectFunctionGroupID ?? null,
                    name: args.name,
                    requiredCount,
                    selectedDays,
                    systemTag: args.systemTag ?? null,
                    locationType: args.locationType ?? null,
                });
                await syncProjectFunctionSeriesDays(tx, {
                    projectID: args.projectID,
                    seriesID: args.id,
                    name: args.name,
                    requiredCount,
                    selectedDays,
                    dayIDsByDate: args.dayIDsByDate,
                });
            }),
            update: defineProjectMutator(updateProjectFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const existing = await tx.run(projectZql.projectFunctionSeries.where("id", args.id).one());
                if (!existing || existing.projectID !== args.projectID) {
                    throw new Error("Function series not found");
                }
                const selectedDays = args.selectedDays === undefined
                    ? (existing.selectedDays ?? [])
                    : normalizeZeroDateArray(args.selectedDays);
                const name = args.name ?? existing.name;
                const requiredCount = args.requiredCount ?? existing.requiredCount;
                await tx.mutate.projectFunctionSeries.update({
                    id: args.id,
                    ...(args.projectFunctionGroupID !== undefined
                        ? { projectFunctionGroupID: args.projectFunctionGroupID ?? null }
                        : {}),
                    ...(args.name !== undefined ? { name } : {}),
                    ...(args.requiredCount !== undefined ? { requiredCount } : {}),
                    ...(args.selectedDays !== undefined ? { selectedDays } : {}),
                    ...(args.systemTag !== undefined
                        ? { systemTag: args.systemTag ?? null }
                        : {}),
                    ...(args.locationType !== undefined
                        ? { locationType: args.locationType ?? null }
                        : {}),
                });
                await syncProjectFunctionSeriesDays(tx, {
                    projectID: args.projectID,
                    seriesID: args.id,
                    name,
                    requiredCount,
                    selectedDays,
                });
            }),
            delete: defineProjectMutator(deleteProjectFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectFunctionSeries.delete({ id: args.id });
            }),
            updateDay: defineProjectMutator(updateProjectFunctionDayInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectFunctions.update({
                    id: args.id,
                    requiredCountOverride: args.requiredCountOverride,
                });
            }),
            initialize: defineProjectMutator(initializeProjectFunctionsInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const fn of args.functions) {
                    const selectedDays = normalizeZeroDateArray(fn.selectedDays);
                    await tx.mutate.projectFunctionSeries.insert({
                        id: fn.id,
                        projectID: args.projectID,
                        projectFunctionGroupID: fn.projectFunctionGroupID ?? null,
                        name: fn.name,
                        requiredCount: fn.requiredCount,
                        selectedDays,
                        systemTag: fn.systemTag ?? null,
                        locationType: fn.locationType ?? null,
                    });
                    await syncProjectFunctionSeriesDays(tx, {
                        projectID: args.projectID,
                        seriesID: fn.id,
                        name: fn.name,
                        requiredCount: fn.requiredCount,
                        selectedDays,
                    });
                    const overridesByDate = new Map(fn.dayOverrides.map((override) => [
                        toZeroDate(override.date),
                        override.count,
                    ]));
                    if (overridesByDate.size === 0)
                        continue;
                    const days = await tx.run(projectZql.projectFunctions.where("seriesID", fn.id));
                    for (const day of days ?? []) {
                        if (!overridesByDate.has(day.functionDate))
                            continue;
                        await tx.mutate.projectFunctions.update({
                            id: day.id,
                            requiredCountOverride: overridesByDate.get(day.functionDate),
                        });
                    }
                }
            }),
        },
        projectPersonTravelDays: {
            upsert: defineProjectMutator(upsertProjectPersonTravelDayInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                const travelDate = toZeroDate(args.travelDate);
                const existing = await tx.run(projectZql.projectPersonTravelDays
                    .where("projectID", args.projectID)
                    .where("personID", args.personID)
                    .where("travelDate", travelDate)
                    .one());
                if (existing) {
                    await tx.mutate.projectPersonTravelDays.update({
                        id: existing.id,
                        remark: args.remark ?? null,
                    });
                    return;
                }
                await tx.mutate.projectPersonTravelDays.insert({
                    id: args.id,
                    projectID: args.projectID,
                    personID: args.personID,
                    travelDate,
                    remark: args.remark ?? null,
                });
            }),
            delete: defineProjectMutator(deleteProjectPersonTravelDayInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.projectPersonTravelDays.delete({ id: args.id });
            }),
        },
        functionAssignments: {
            create: defineProjectMutator(createFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.functionAssignments.insert({
                    id: args.id,
                    functionID: args.functionID,
                    personID: args.personID,
                    remark: args.remark ?? null,
                });
            }),
            bulkAssignSeries: defineProjectMutator(bulkAssignFunctionSeriesInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                for (const functionID of args.functionIDs) {
                    const existing = await tx.run(projectZql.functionAssignments
                        .where("functionID", functionID)
                        .where("personID", args.personID)
                        .one());
                    if (existing)
                        continue;
                    await tx.mutate.functionAssignments.insert({
                        id: crypto.randomUUID(),
                        functionID,
                        personID: args.personID,
                        remark: null,
                    });
                }
            }),
            update: defineProjectMutator(updateFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.functionAssignments.update({
                    id: args.id,
                    remark: args.remark ?? null,
                });
            }),
            delete: defineProjectMutator(deleteFunctionAssignmentInputSchema, async ({ tx, ctx, args, }) => {
                await assertProjectWriteAccess(tx, ctx, args.projectID);
                await tx.mutate.functionAssignments.delete({ id: args.id });
            }),
        },
    };
}
